import React, { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { MdBusiness, MdGroups, MdPerson } from 'react-icons/md';
import { useAuth } from '../../Context/AuthContext';
import { PermissionCodes, permissionHas, resolveUserPermissionCodes } from '../../Utils/Permissions';
import { APPRoutes } from '../../Utils/Route';
import { getEligibilityVerification, postBulkEligibility, postEligibility } from '../../Services/Claim360Service';
import SearchableDropdown from '../../Components/Subscription/SearchableDropdown';
import { OrganizationSelect, PayerSelect, ProviderSelect, usePayers } from '../../Components/Claim360/StediSelects';
import { BulkSubscriberImport, DateInput, FormSection, StediFormShell, TextInput } from '../../Components/Claim360/StediFormParts';
import EligibilityResultModal from '../../Components/Claim360/EligibilityResultModal';
import { insuranceMappings, splitPatientName } from '../../Utils/stediUtils';

// Mental health is what the eligibility record stores (coverage, copay, deductible).
const DEFAULT_SERVICE_TYPE_CODES = ['MH'];

// Common X12 service type codes; any other 1-2 character code can be typed in.
const SERVICE_TYPE_OPTIONS = [
  { value: 'MH', label: 'MH - Mental Health' },
  { value: '30', label: '30 - Health Benefit Plan Coverage' },
  { value: 'A4', label: 'A4 - Psychiatric' },
  { value: 'A6', label: 'A6 - Psychotherapy' },
  { value: 'A7', label: 'A7 - Psychiatric Inpatient' },
  { value: 'A8', label: 'A8 - Psychiatric Outpatient' },
  { value: 'AI', label: 'AI - Substance Abuse' },
  { value: '98', label: '98 - Professional (Physician) Visit - Office' },
];

const emptyForm = {
  organizationId: '',
  firstName: '',
  lastName: '',
  payerId: '',
  memberId: '',
  dob: '',
  providerId: '',
  serviceTypeCodes: DEFAULT_SERVICE_TYPE_CODES,
};

const providerRules = {
  organizationId: Yup.string().required('Organization is required'),
  providerId: Yup.string().required('Provider is required'),
  serviceTypeCodes: Yup.array().of(Yup.string().trim().matches(/^[A-Za-z0-9]{1,2}$/, 'Use a 1-2 character service type code')),
};

const validationSchema = Yup.object({
  ...providerRules,
  firstName: Yup.string().trim().required('First name is required'),
  lastName: Yup.string().trim().required('Last name is required'),
  payerId: Yup.string().trim().required('Payer is required'),
  memberId: Yup.string().trim().required('Member ID is required'),
  dob: Yup.string().required('Date of birth is required'),
});

const bulkValidationSchema = Yup.object(providerRules);

const serviceCodes = (values) => values.serviceTypeCodes.map((code) => String(code).trim().toUpperCase()).filter(Boolean);

// Real-time body, in Stedi's field names (what /stedi/eligibility validates). The provider is not sent:
// the API fills it from the selected organization (its name) and provider (its NPI).
function buildPayload(values) {
  const codes = serviceCodes(values);
  return {
    payerId: values.payerId.trim(),
    subscriber: {
      memberId: values.memberId.trim(),
      dateOfBirth: values.dob,
      name: { person: { firstName: values.firstName.trim(), lastName: values.lastName.trim() } },
    },
    ...(codes.length ? { encounter: { services: codes.map((value) => ({ system: 'STC', value })) } } : {}),
  };
}

// Multipart body of /stedi/bulk/eligibility: the uploaded sheet plus the settings every row shares.
function buildBulkForm(values, file, rows) {
  const form = new FormData();
  form.append('file', file);
  form.append('insuranceMappings', JSON.stringify(insuranceMappings(rows)));
  form.append('providerId', values.providerId);
  if (values.payerId) form.append('payerId', values.payerId.trim());
  form.append('serviceTypeCodes', serviceCodes(values).join(','));
  return form;
}

function mapRecordToForm(record) {
  const { firstName, lastName } = splitPatientName(record?.patientName);
  return {
    ...emptyForm,
    organizationId: record?.organizationId ?? '',
    firstName,
    lastName,
    payerId: record?.payerId ?? '',
    memberId: record?.memberId ?? '',
    dob: record?.dateOfBirth ?? '',
  };
}

const TABS = [
  { id: 'single', label: 'Single Check' },
  { id: 'bulk', label: 'Bulk Upload' },
];

export default function EligibilityVerificationForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isEdit = !!id;
  const { token, permissions, decodedToken } = useAuth();

  const userPermissionCodes = useMemo(
    () => resolveUserPermissionCodes({ permissions, decodedToken }),
    [permissions, decodedToken],
  );

  const canSave = permissionHas(
    userPermissionCodes,
    isEdit ? PermissionCodes.ELIGIBILITY_VERIFICATION_EDIT : PermissionCodes.ELIGIBILITY_VERIFICATION_CREATE,
    decodedToken,
  );

  const [loading, setLoading] = useState(isEdit);
  const [activeTab, setActiveTab] = useState('single');
  const [bulkSubscribers, setBulkSubscribers] = useState([]);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkErrors, setBulkErrors] = useState([]);
  const [payers, payersLoading] = usePayers(token);
  // Result of the last single check, shown in a modal before going back to the list.
  const [result, setResult] = useState(null);
  const isBulk = activeTab === 'bulk';

  const formik = useFormik({
    initialValues: emptyForm,
    validate: async (values) => {
      const schema = isBulk ? bulkValidationSchema : validationSchema;
      const errors = {};

      try {
        await schema.validate(values, { abortEarly: false });
      } catch (error) {
        if (!(error instanceof Yup.ValidationError)) {
          throw error;
        }

        error.inner.forEach(({ path, message }) => {
          if (path && !errors[path]) {
            errors[path] = message;
          }
        });
      }

      if (isBulk) {
        if (bulkSubscribers.length === 0 || !bulkFile) {
          errors.bulkSubscribers = 'Import an Excel file with subscriber details before submitting.';
        } else if (!values.payerId && bulkSubscribers.some((row) => !row.insuranceName)) {
          errors.payerId = 'Pick a default payer for rows that have no Insurance Name.';
        }
      }

      return errors;
    },
    onSubmit: async (values) => {
      setBulkErrors([]);
      try {
        if (isBulk) {
          const response = await postBulkEligibility(buildBulkForm(values, bulkFile, bulkSubscribers), values.organizationId, token);
          toast.success(response?.message || 'Bulk eligibility check started. The report will be emailed to you.');
          navigate(APPRoutes.ELIGIBILITY_VERIFICATION);
        } else {
          const response = await postEligibility(buildPayload(values), values.organizationId, values.providerId, token);
          setResult(response?.data ?? null);
        }
      } catch (error) {
        console.error('Failed to run eligibility check:', error);
        // Unrecognized insurance names are flagged on the import preview instead of in a toast.
        if (isBulk && error.data?.error_type === 'UNKNOWN_INSURANCE') {
          setBulkErrors(error.data.unknown_insurances ?? []);
          return;
        }
        toast.error(error.message || 'Unable to run eligibility check.');
      }
    },
  });
  const { setValues } = formik;

  const loadEligibility = useCallback(async () => {
    if (!isEdit) return;

    try {
      setLoading(true);
      const stateRecord = location.state?.eligibility;
      const record = stateRecord && String(stateRecord.id) === String(id) && stateRecord.organizationId
        ? stateRecord
        : (await getEligibilityVerification(id, token))?.data;
      if (!record) throw new Error('Eligibility record was not found.');
      setValues(mapRecordToForm(record));
    } catch (error) {
      console.error('Failed to load eligibility verification:', error);
      toast.error(error.message || 'Failed to load eligibility verification.');
    } finally {
      setLoading(false);
    }
  }, [id, isEdit, location.state, token, setValues]);

  React.useEffect(() => {
    if (!isEdit || !token) {
      setLoading(false);
      return;
    }
    loadEligibility();
  }, [isEdit, token, loadEligibility]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setBulkErrors([]);
    formik.setErrors({});
    formik.setTouched({}, false);
  };

  const codeError = Object.entries(formik.errors).find(([key]) => key.startsWith('serviceTypeCodes'))?.[1];

  return (
    <StediFormShell
      title={isEdit ? 'Re-check Eligibility' : 'Check Eligibility'}
      subtitle="Runs a Stedi eligibility check and saves the patient to the selected organization"
      onBack={() => navigate(-1)}
      tabs={isEdit ? null : TABS}
      activeTab={activeTab}
      onTabChange={handleTabChange}
      loading={loading}
      canSave={canSave}
      deniedMessage="You do not have permission to save eligibility verification."
      onSubmit={formik.handleSubmit}
      isSubmitting={formik.isSubmitting}
      submitLabel={isBulk ? 'Submit Bulk Check' : 'Check Eligibility'}
    >
      <FormSection
        icon={MdBusiness}
        title="Organization & Payer"
        subtitle="Where the patient is saved, which payer to ask and for which services; MH fills mental health coverage, copay and deductible"
      >
        <div className="col-12 col-md-6">
          <OrganizationSelect formik={formik} token={token} />
        </div>
        <div className="col-12 col-md-6">
          <ProviderSelect formik={formik} token={token} label="Provider NPI" />
        </div>
        <div className="col-12 col-md-6">
          <PayerSelect
            formik={formik}
            name="payerId"
            payers={payers}
            loading={payersLoading}
            label={isBulk ? 'Default Payer' : 'Insurance Payer'}
            required={!isBulk}
          />
        </div>
        {/* MH fills mental health coverage, copay and deductible. */}
        <div className="col-12 col-md-6">
          <SearchableDropdown
            label="Service Type Codes"
            placeholder="Select service types"
            options={SERVICE_TYPE_OPTIONS}
            value={formik.values.serviceTypeCodes}
            onChange={(codes) => formik.setFieldValue('serviceTypeCodes', codes)}
            multiple
            allowCustom
            showSearch
            error={codeError || ''}
          />
          {formik.values.serviceTypeCodes.length > 0 && (
            <div className="d-flex flex-wrap gap-2 mt-2">
              {formik.values.serviceTypeCodes.map((code) => (
                <span key={code} className="badge rounded-pill bg-light text-dark border fw-semibold px-3 py-2">{code}</span>
              ))}
            </div>
          )}
        </div>
      </FormSection>

      {isBulk ? (
        <FormSection icon={MdGroups} title="Patients" subtitle="Checked in the background; the file is emailed back to you with the results added">
          <BulkSubscriberImport
            kind="eligibility"
            rows={bulkSubscribers}
            file={bulkFile}
            onRowsChange={(rows, file) => {
              setBulkSubscribers(rows);
              setBulkFile(file);
              setBulkErrors([]);
            }}
            defaultPayerId={formik.values.payerId}
            payers={payers}
            error={formik.submitCount > 0 ? formik.errors.bulkSubscribers : ''}
            serverErrors={bulkErrors}
          />
        </FormSection>
      ) : (
        <FormSection icon={MdPerson} title="Patient" subtitle="Matched to an existing patient by name and date of birth">
          <TextInput formik={formik} name="firstName" label="First Name" placeholder="e.g. Jane" autoComplete="off" />
          <TextInput formik={formik} name="lastName" label="Last Name" placeholder="e.g. Doe" autoComplete="off" />
          <TextInput formik={formik} name="memberId" label="Member ID" placeholder="From the insurance card" autoComplete="off" />
          <DateInput formik={formik} name="dob" label="Date of Birth" maxDate={dayjs()} />
        </FormSection>
      )}

      {/* Closing opens the saved record; the list is the fallback if it could not be saved. */}
      <EligibilityResultModal
        result={result}
        onClose={() => navigate(result?.verificationId
          ? APPRoutes.ELIGIBILITY_VERIFICATION_VIEW.replace(':id', result.verificationId)
          : APPRoutes.ELIGIBILITY_VERIFICATION)}
      />
    </StediFormShell>
  );
}
